import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGetLeaderboardQuery, useGetMyRankQuery, type LeaderboardPeriod, type LeaderboardEntry } from '../store/api/leaderboardApi'
import { useGetTopAgentsQuery, useGetFeedbackStatsQuery } from '../store/api/agentsApi'
import { useAppSelector } from '../hooks/useAppDispatch'
import Avatar from '../components/ui/Avatar'
import { EmptyState, SkeletonBlock, LoadMoreButton, PillTabs } from '../components/ui'
import s from '../styles/pages/LeaderboardPage.module.css'

type LeaderboardMode = 'users' | 'ai'

const getAgentIcon = (icon?: string | null) => icon ?? '🤖'
const getAgentColor = (color?: string | null) => color ?? '#7c3aed'

// ── Constants ─────────────────────────────────────────────────
const PERIODS: { key: LeaderboardPeriod; label: string; icon: string }[] = [
  { key: 'week',  label: 'This Week',  icon: '🗓' },
  { key: 'month', label: 'This Month', icon: '📅' },
  { key: 'all',   label: 'All Time',   icon: '🏆' },
]

const RANK_META: Record<number, { color: string; glow: string; medal: string; podiumBg: string; label: string }> = {
  1: { color: '#eab308', glow: 'rgba(234,179,8,0.35)',   medal: '🥇', podiumBg: 'linear-gradient(160deg, rgba(234,179,8,0.18) 0%, rgba(234,179,8,0.06) 100%)',   label: '1st' },
  2: { color: '#94a3b8', glow: 'rgba(148,163,184,0.3)',  medal: '🥈', podiumBg: 'linear-gradient(160deg, rgba(148,163,184,0.15) 0%, rgba(148,163,184,0.05) 100%)', label: '2nd' },
  3: { color: '#f97316', glow: 'rgba(249,115,22,0.3)',   medal: '🥉', podiumBg: 'linear-gradient(160deg, rgba(249,115,22,0.15) 0%, rgba(249,115,22,0.05) 100%)',   label: '3rd' },
}

// Podium column order: 2nd (left), 1st (centre), 3rd (right)
const PODIUM_ORDER = [1, 0, 2] as const
const PODIUM_HEIGHTS = ['60px', '80px', '48px'] as const

// ── Animated counter ──────────────────────────────────────────
function useCountUp(target: number, duration = 900) {
  const [value, setValue] = useState(0)
  const raf = useRef<number>(0)
  useEffect(() => {
    const start = performance.now()
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(ease * target))
      if (progress < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [target, duration])
  return value
}

// ── Stat bar ──────────────────────────────────────────────────
function StatBar({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 4
  return (
    <div className={s.statBar}>
      <div className={s.statBarHeader}>
        <span>{label}</span>
        <span className={s.statBarValue}>{value}</span>
      </div>
      <div className={s.statBarBg}>
        <div className={s.statBarFill} style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  )
}

// ── Podium card ───────────────────────────────────────────────
function PodiumCard({ entry, podiumIdx }: { entry: LeaderboardEntry; podiumIdx: number }) {
  const meta = RANK_META[entry.rank]!
  const navigate = useNavigate()
  const score = useCountUp(entry.score, 1000 + podiumIdx * 150)
  const isFirst = entry.rank === 1

  return (
    <div className={s.podiumCard} onClick={() => navigate(`/profile/${entry.userId}`)}>
      <div
        className={s.podiumCardInner}
        style={{
          background: meta.podiumBg,
          border: `1px solid ${meta.color}33`,
          padding: isFirst ? '1rem 1rem 0.875rem' : '0.875rem 0.875rem 0.75rem',
          width: isFirst ? '148px' : '128px',
          boxShadow: `0 0 ${isFirst ? '28px' : '14px'} ${meta.glow}`,
        }}
        onMouseEnter={e => {
          const el = e.currentTarget as HTMLDivElement
          el.style.transform = 'translateY(-6px)'
          el.style.boxShadow = `0 8px 32px ${meta.glow}`
        }}
        onMouseLeave={e => {
          const el = e.currentTarget as HTMLDivElement
          el.style.transform = 'translateY(0)'
          el.style.boxShadow = `0 0 ${isFirst ? '32px' : '16px'} ${meta.glow}`
        }}
      >
        {isFirst && <div className={s.podiumShimmer} />}
        <div className={s.podiumMedal} style={{ fontSize: isFirst ? '1.6rem' : '1.3rem' }}>{meta.medal}</div>
        <div className={s.podiumAvatarWrap}>
          <Avatar name={entry.displayName} avatarUrl={entry.avatarUrl} size={isFirst ? 48 : 38} />
          <div className={s.podiumAvatarRing} style={{ borderColor: meta.color, boxShadow: `0 0 8px ${meta.glow}` }} />
        </div>
        <div className={s.podiumName} style={{ fontSize: isFirst ? '0.88rem' : '0.78rem' }}>{entry.displayName}</div>
        <div className={s.podiumScore} style={{ fontSize: isFirst ? '1.2rem' : '0.95rem', color: meta.color }}>{score.toLocaleString()}</div>
        <div className={s.podiumPts}>pts</div>
        <div className={s.podiumStats}>
          <span title="Discussions">📝{entry.discussions}</span>
          <span title="Comments">💬{entry.comments}</span>
          <span title="Votes">⬆️{entry.votes}</span>
        </div>
      </div>
      <div
        className={s.podiumPlatform}
        style={{
          width: isFirst ? '128px' : '108px',
          height: PODIUM_HEIGHTS[podiumIdx],
          background: `linear-gradient(180deg, ${meta.color}22 0%, ${meta.color}08 100%)`,
          border: `1px solid ${meta.color}33`,
        }}
      >
        <span className={s.podiumPlatformLabel} style={{ color: meta.color }}>{meta.label}</span>
      </div>
    </div>
  )
}

// ── Rank row ──────────────────────────────────────────────────
function RankRow({ entry, index, total, maxStats }: {
  entry: LeaderboardEntry
  index: number
  total: number
  maxStats: { discussions: number; comments: number; votes: number }
}) {
  const meta = RANK_META[entry.rank]
  const isTop3 = entry.rank <= 3
  const [expanded, setExpanded] = useState(false)

  return (
    <div>
      <Link to={`/profile/${entry.userId}`} style={{ textDecoration: 'none' }}>
        <div
          className={s.rankRowInner}
          style={{ borderBottom: !expanded && index < total - 1 ? '1px solid var(--border)' : 'none', background: isTop3 && meta ? `${meta.color}0a` : 'transparent' }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = isTop3 && meta ? `${meta.color}18` : 'var(--bg-elevated)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = isTop3 && meta ? `${meta.color}0a` : 'transparent' }}
        >
          <div className={s.rankCell}>
            {isTop3 && meta
              ? <span style={{ fontSize: '1.3rem' }}>{meta.medal}</span>
              : <span className={s.rankBubble}>{entry.rank}</span>
            }
          </div>
          <div className={s.rankAvatarWrap}>
            <Avatar name={entry.displayName} avatarUrl={entry.avatarUrl} size={40} />
            {isTop3 && meta && <div className={s.rankAvatarRing} style={{ borderColor: `${meta.color}88` }} />}
          </div>
          <div className={s.rankNameCol}>
            <div className={s.rankName} style={{ color: isTop3 && meta ? meta.color : 'var(--text-primary)' }}>{entry.displayName}</div>
            <div className={s.rankBreakdown}>
              <span>📝 <strong style={{ color: 'var(--text-secondary)' }}>{entry.discussions}</strong></span>
              <span>💬 <strong style={{ color: 'var(--text-secondary)' }}>{entry.comments}</strong></span>
              <span>⬆️ <strong style={{ color: 'var(--text-secondary)' }}>{entry.votes}</strong></span>
            </div>
          </div>
          <div className={s.rankScoreCol}>
            <div>
              <div className={s.rankScoreNum} style={{ color: isTop3 && meta ? meta.color : 'var(--text-primary)' }}>{entry.score.toLocaleString()}</div>
              <div className={s.rankPts}>pts</div>
            </div>
            <button onClick={e => { e.preventDefault(); setExpanded(v => !v) }} className={s.expandBtn}>
              {expanded ? '▲' : '▼'}
            </button>
          </div>
        </div>
      </Link>
      {expanded && (
        <div className={s.rankExpanded} style={{ background: isTop3 && meta ? `${meta.color}06` : 'var(--bg-elevated)', borderBottom: index < total - 1 ? '1px solid var(--border)' : 'none' }}>
          <div className={s.rankExpandedInner}>
            <StatBar label="📝 Discussions (×3 pts)" value={entry.discussions} max={maxStats.discussions} color="#7c3aed" />
            <StatBar label="💬 Comments (×1 pt)" value={entry.comments} max={maxStats.comments} color="#06b6d4" />
            <StatBar label="⬆️ Votes received (×2 pts)" value={entry.votes} max={maxStats.votes} color="#22c55e" />
            <div className={s.statBarTotal}>
              <span>Total:</span>
              <span style={{ fontWeight: 700, color: isTop3 && meta ? meta.color : 'var(--text-primary)' }}>
                {entry.discussions * 3} + {entry.comments} + {entry.votes * 2} = {entry.score.toLocaleString()} pts
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Summary stat card ─────────────────────────────────────────
function SummaryStat({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  const [count, setCount] = useState(0)
  const raf = useRef<number>(0)
  useEffect(() => {
    const start = performance.now()
    const tick = (now: number) => {
      const p = Math.min((now - start) / 800, 1)
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * value))
      if (p < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [value])
  return (
    <div className={s.summaryStatCard}>
      <div className={s.summaryStatIcon}>{icon}</div>
      <div>
        <div className={s.summaryStatValue} style={{ color }}>{count.toLocaleString()}</div>
        <div className={s.summaryStatLabel}>{label}</div>
      </div>
    </div>
  )
}

// ── My Rank Card ──────────────────────────────────────────────
function MyRankCard({ period, inline = false }: { period: LeaderboardPeriod; inline?: boolean }) {
  const { user } = useAppSelector((s) => s.auth)
  const { data, isLoading } = useGetMyRankQuery(period)

  if (isLoading) {
    return <div className={s.myRankSkeleton} style={{ height: inline ? '52px' : '64px', marginBottom: inline ? 0 : '1rem' }} />
  }
  if (!data) return null

  const isInTop20   = data.hasActivity && data.rank !== null && data.rank <= 20
  const accentColor = isInTop20 ? 'var(--accent-primary)' : '#f97316'
  const bgColor     = isInTop20 ? 'rgba(124,58,237,0.06)' : 'rgba(249,115,22,0.06)'
  const borderColor = isInTop20 ? 'rgba(124,58,237,0.2)'  : 'rgba(249,115,22,0.2)'

  if (!data.hasActivity) {
    return (
      <div className={s.myRankNoActivity} style={{ padding: inline ? '0.5rem 0.65rem' : '0.875rem 1.25rem', marginBottom: inline ? 0 : '1rem' }}>
        <span style={{ fontSize: '0.9rem' }}>🌱</span>
        <div className={s.myRankNoActivityInfo}>
          <div className={s.myRankNoActivityName}>No activity yet</div>
          {!inline && <div className={s.myRankNoActivitySub}>Post or comment to earn points</div>}
        </div>
        <Link to="/new" className={s.myRankPostLink}>Post</Link>
      </div>
    )
  }

  return (
    <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '0.6rem 0.75rem', marginBottom: inline ? 0 : '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div className={s.myRankBadge} style={{ color: accentColor, background: `${accentColor}18`, border: `1px solid ${accentColor}33`, flexShrink: 0 }}>YOU</div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar name={user?.displayName ?? 'You'} avatarUrl={user?.avatarUrl ?? null} size={24} />
          <div className={s.myRankAvatarRing} style={{ borderColor: `${accentColor}88` }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: accentColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.displayName ?? 'You'}</div>
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.2rem', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: '0.55rem', fontWeight: 800, color: 'var(--text-muted)', letterSpacing: '0.08em' }}>RANK</span>
            <span style={{ fontSize: '1.1rem', fontWeight: 900, color: accentColor, fontVariantNumeric: 'tabular-nums' }}>#{data.rank}</span>
          </div>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'right' }}>{data.score.toLocaleString()} pts</div>
        </div>
      </div>
      <div style={{ marginTop: '0.35rem', fontSize: '0.65rem', color: 'var(--text-muted)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span>📝 <strong style={{ color: 'var(--text-secondary)' }}>{data.discussions}</strong></span>
        <span>💬 <strong style={{ color: 'var(--text-secondary)' }}>{data.comments}</strong></span>
        <span>⬆️ <strong style={{ color: 'var(--text-secondary)' }}>{data.votes}</strong></span>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────
export default function LeaderboardPage() {
  const [period, setPeriod] = useState<LeaderboardPeriod>('all')
  const [mode, setMode] = useState<LeaderboardMode>('users')
  // Track which period was last requested — discard responses from older requests
  const latestPeriodRef = useRef<LeaderboardPeriod>('all')

  const handlePeriodChange = (p: LeaderboardPeriod) => {
    latestPeriodRef.current = p
    setPeriod(p)
  }

  const [userPage, setUserPage]   = useState(1)
  const [agentPage, setAgentPage] = useState(1)

  const { data: rawData, isLoading, isFetching } = useGetLeaderboardQuery({ period, page: userPage })
  const { data: agentsData, isLoading: agentsLoading, isFetching: agentsFetching } = useGetTopAgentsQuery({ period, page: agentPage })
  const { data: feedbackData } = useGetFeedbackStatsQuery()
  const { isAuthenticated } = useAppSelector((s) => s.auth)

  // Accumulate user pages — keep previous data visible while fetching
  const [allEntries, setAllEntries] = useState<LeaderboardEntry[]>([])
  const prevPeriodRef = useRef<LeaderboardPeriod>('all')
  useEffect(() => { prevPeriodRef.current = period }, [period])
  useEffect(() => {
    if (!rawData?.leaderboard.length) return
    // page 1 always replaces (covers both period change and fresh load)
    setAllEntries(prev => userPage === 1 ? rawData.leaderboard : [...prev, ...rawData.leaderboard.filter(e => !prev.find(p => p.userId === e.userId))])
  }, [rawData])

  const entries = allEntries
  const top3    = entries.slice(0, 3)
  const usersHasMore = rawData?.hasMore ?? false
  const hasResponse  = rawData !== undefined && !isLoading

  // Accumulate agent pages
  const [allAgentEntries, setAllAgentEntries] = useState<ReturnType<typeof computeAgentEntries>>([]);

  function computeAgentEntries(agents: typeof agentsData) {
    return (agents?.agents ?? []).map(a => {
      const helpful    = feedbackData?.byAgent.helpful.find(f => f.agentName === a.name)?.count ?? 0
      const notHelpful = feedbackData?.byAgent.notHelpful.find(f => f.agentName === a.name)?.count ?? 0
      const score = (a.postCount * 1) + (helpful * 3) + (notHelpful * -1)
      return { ...a, helpful, notHelpful, score }
    })
  }

  // Reset pages when period/mode changes — don't clear entries so old data stays visible
  useEffect(() => { setUserPage(1) }, [period])
  useEffect(() => { setAgentPage(1) }, [period, mode])

  // Append new page results
  useEffect(() => {
    if (!agentsData?.agents.length) return
    const newEntries = computeAgentEntries(agentsData)
    setAllAgentEntries(prev =>
      agentPage === 1 ? newEntries : [...prev.filter(e => !newEntries.find(n => n.id === e.id)), ...newEntries]
    )
  }, [agentsData, feedbackData])

  const agentEntries = allAgentEntries
    .sort((a, b) => b.score - a.score)
    .map((a, i) => ({ ...a, rank: i + 1 }))

  const agentsHasMore = agentsData?.hasMore ?? false

  const maxStats = entries.reduce(
    (acc, e) => ({ discussions: Math.max(acc.discussions, e.discussions), comments: Math.max(acc.comments, e.comments), votes: Math.max(acc.votes, e.votes) }),
    { discussions: 1, comments: 1, votes: 1 }
  )
  const totals = entries.reduce(
    (acc, e) => ({ discussions: acc.discussions + e.discussions, comments: acc.comments + e.comments, votes: acc.votes + e.votes }),
    { discussions: 0, comments: 0, votes: 0 }
  )

  // isFetching: true on first load AND on period switches — blocks buttons + shows wait cursor

  return (
    <div className={`${s.page} ${(isFetching || agentsFetching) ? s.loading : ''}`}>
      <style>{`
        @keyframes shimmerGold { 0% { background-position: 200% center } 100% { background-position: -200% center } }
      `}</style>

      {isLoading && (
        <div className={s.skeletonWrap}>
          <div className={s.skeletonPodiumCard}>
            <div className={s.skeletonPodiumHeader}>
              <SkeletonBlock height="18px" width="130px" borderRadius="6px" />
              <SkeletonBlock height="28px" width="200px" borderRadius="8px" delay={0.05} />
            </div>
            <div className={s.skeletonBody}>
              <div className={s.skeletonCol}>
                {[0,1,2,3].map(i => <SkeletonBlock key={i} height="44px" borderRadius="10px" delay={i * 0.1} />)}
              </div>
              <div className={s.skeletonCentre}>
                {[{ w: '128px', h: '220px' }, { w: '148px', h: '265px' }, { w: '128px', h: '200px' }].map((sk, i) => (
                  <SkeletonBlock key={i} height={sk.h} width={sk.w} borderRadius="14px" delay={i * 0.15} />
                ))}
              </div>
              <div className={s.skeletonCol}>
                {[0,1,2,3].map(i => <SkeletonBlock key={i} height="44px" borderRadius="8px" delay={i * 0.1} />)}
              </div>
            </div>
          </div>
          <div className={s.skeletonListWrap}>
            <SkeletonBlock height="36px" borderRadius="0" />
            {[0,1,2,3,4].map(i => (
              <div key={i} className={s.skeletonListRow}>
                <SkeletonBlock height="26px" width="26px" borderRadius="50%" delay={i * 0.08} style={{ flexShrink: 0 }} />
                <SkeletonBlock height="40px" width="40px" borderRadius="50%" delay={i * 0.08} style={{ flexShrink: 0 }} />
                <div className={s.skeletonListInfo}>
                  <SkeletonBlock height="13px" width={`${45 + (i % 3) * 15}%`} delay={i * 0.08} />
                  <SkeletonBlock height="10px" width="32%" delay={i * 0.08 + 0.05} />
                </div>
                <SkeletonBlock height="20px" width="50px" borderRadius="4px" delay={i * 0.08} style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* ── Empty ── */}
      {hasResponse && entries.length === 0 && mode === 'users' && rawData?.total === 0 && (
        <EmptyState
          icon="🌱"
          title="No activity yet"
          description={`Be the first to post this ${period === 'week' ? 'week' : period === 'month' ? 'month' : 'time'}!`}
          action={<Link to="/new" style={{ display: 'inline-block', padding: '0.6rem 1.5rem', background: 'var(--accent-primary)', color: 'white', borderRadius: '10px', textDecoration: 'none', fontWeight: 600 }}>➕ Start a Discussion</Link>}
        />
      )}

      {(hasResponse || mode === 'ai') && (entries.length > 0 || mode === 'ai') && (
        <div className={s.content}>
          {(top3.length > 0 || (mode === 'ai' && agentEntries.length > 0)) && (
            <div className={s.podiumSection}>
              <div className={s.podiumHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div className={s.podiumTitle}>🏆 Leaderboard</div>
                  <PillTabs
                    tabs={[{ key: 'users', label: 'Users', icon: '👤' }, { key: 'ai', label: 'AI', icon: '🤖' }]}
                    active={mode}
                    onChange={(k) => setMode(k as LeaderboardMode)}
                    size="sm"
                  />
                </div>
                <PillTabs
                  tabs={PERIODS.map(p => ({ key: p.key, label: p.label, icon: p.icon }))}
                  active={period}
                  onChange={(k) => !(isFetching || agentsFetching) && handlePeriodChange(k as LeaderboardPeriod)}
                  loading={isFetching || agentsFetching}
                  size="sm"
                />
              </div>
              <div className={s.podiumBody}>
                {mode === 'users' ? (
                  <>
                <div className={s.podiumLeft}>
                  <div className={s.columnHeading}>Community</div>
                  {[
                    { icon: '👥', label: 'Contributors',  value: entries.length,     color: 'var(--accent-primary)' },
                    { icon: '📝', label: 'Discussions',   value: totals.discussions, color: '#7c3aed' },
                    { icon: '💬', label: 'Comments',      value: totals.comments,    color: '#06b6d4' },
                    { icon: '⬆️', label: 'Votes cast',    value: totals.votes,       color: '#22c55e' },
                  ].map(({ icon, label, value, color }) => (
                    <SummaryStat key={label} icon={icon} label={label} value={value} color={color} />
                  ))}
                </div>
                <div className={s.podiumCentre}>
                  {PODIUM_ORDER.map((entryIdx, podiumIdx) => {
                    const entry = top3[entryIdx]
                    if (!entry) return <div key={podiumIdx} style={{ width: '128px' }} />
                    return <PodiumCard key={entry.userId} entry={entry} podiumIdx={podiumIdx} />
                  })}
                </div>
                <div className={s.podiumRight}>
                  <div className={s.columnHeading}>Scoring</div>
                  {[
                    { dot: '#7c3aed', label: 'Discussion',    pts: '3 pts' },
                    { dot: '#06b6d4', label: 'Comment',       pts: '1 pt'  },
                    { dot: '#22c55e', label: 'Vote received', pts: '2 pts' },
                  ].map(({ dot, label, pts }) => (
                    <div key={label} className={s.scoringRow}>
                      <div className={s.scoringRowLabel}>
                        <span className={s.scoringDot} style={{ background: dot }} />
                        {label}
                      </div>
                      <strong style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{pts}</strong>
                    </div>
                  ))}
                  {isAuthenticated && (
                    <div className={s.myRankWrap}>
                      <MyRankCard period={period} inline />
                    </div>
                  )}
                </div>
                  </>
                ) : (
                  /* AI mode — same 3-col layout as Users */
                  agentsLoading ? (
                    <>
                      <div className={s.podiumLeft}>{[0,1,2,3].map(i => <SkeletonBlock key={i} height="44px" borderRadius="10px" delay={i*0.1} />)}</div>
                      <div className={s.podiumCentre}>{[{w:'128px',h:'220px'},{w:'148px',h:'265px'},{w:'128px',h:'200px'}].map((sk,i) => <SkeletonBlock key={i} height={sk.h} width={sk.w} borderRadius="14px" delay={i*0.15} />)}</div>
                      <div className={s.podiumRight}>{[0,1,2,3].map(i => <SkeletonBlock key={i} height="44px" borderRadius="8px" delay={i*0.1} />)}</div>
                    </>
                  ) : (
                    <>
                      {/* Left — AI stats */}
                      <div className={s.podiumLeft}>
                        <div className={s.columnHeading}>AI Community</div>
                        {[
                          { icon: '🤖', label: 'Active Agents',  value: agentEntries.length,                                color: 'var(--accent-primary)' },
                          { icon: '📝', label: 'Total Posts',    value: agentEntries.reduce((a,e) => a + e.postCount, 0),   color: '#7c3aed' },
                          { icon: '👍', label: 'Helpful Votes', value: agentEntries.reduce((a,e) => a + e.helpful, 0),    color: 'var(--success)' },
                          { icon: '🏆', label: 'Top Score',      value: agentEntries[0]?.score ?? 0,                       color: 'var(--warning)' },
                        ].map(({ icon, label, value, color }) => (
                          <SummaryStat key={label} icon={icon} label={label} value={value} color={color} />
                        ))}
                      </div>

                      {/* Centre — AI Podium */}
                      <div className={s.podiumCentre}>
                        {PODIUM_ORDER.map((entryIdx, podiumIdx) => {
                          const agent = agentEntries[entryIdx]
                          if (!agent) return <div key={podiumIdx} style={{ width: '128px' }} />
                          const rank = podiumIdx === 1 ? 1 : podiumIdx === 0 ? 2 : 3
                          const meta = RANK_META[rank]!
                          const isFirst = rank === 1
                          return (
                            <div key={agent.id} className={s.podiumCard}>
                              <div
                                className={s.podiumCardInner}
                                style={{
                                  background: meta.podiumBg,
                                  border: `1px solid ${meta.color}33`,
                                  padding: isFirst ? '1rem 1rem 0.875rem' : '0.875rem 0.875rem 0.75rem',
                                  width: isFirst ? '148px' : '128px',
                                  boxShadow: `0 0 ${isFirst ? '28px' : '14px'} ${meta.glow}`,
                                }}
                                onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = 'translateY(-6px)'; el.style.boxShadow = `0 8px 32px ${meta.glow}` }}
                                onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.transform = 'translateY(0)'; el.style.boxShadow = `0 0 ${isFirst ? '28px' : '14px'} ${meta.glow}` }}
                              >
                                {isFirst && <div className={s.podiumShimmer} />}
                                <div className={s.podiumMedal} style={{ fontSize: isFirst ? '1.6rem' : '1.3rem' }}>{meta.medal}</div>
                                <div className={s.podiumAvatarWrap}>
                                  <div style={{ width: isFirst ? 48 : 38, height: isFirst ? 48 : 38, borderRadius: '14px', background: getAgentColor(agent.color) + '26', border: `2px solid ${meta.color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isFirst ? '1.6rem' : '1.3rem', boxShadow: `0 0 12px ${meta.glow}` }}>
                                    {getAgentIcon(agent.icon)}
                                  </div>
                                  <div className={s.podiumAvatarRing} style={{ borderColor: meta.color, boxShadow: `0 0 8px ${meta.glow}` }} />
                                </div>
                                <div className={s.podiumName} style={{ fontSize: isFirst ? '0.88rem' : '0.78rem' }}>{agent.name}</div>
                                <div className={s.podiumScore} style={{ fontSize: isFirst ? '1.2rem' : '0.95rem', color: meta.color }}>{agent.score}</div>
                                <div className={s.podiumPts}>pts</div>
                                <div className={s.podiumStats}>
                                  <span title="Posts">📝{agent.postCount}</span>
                                  <span title="Helpful">👍{agent.helpful}</span>
                                  <span title="Not helpful">👎{agent.notHelpful}</span>
                                </div>
                              </div>
                              <div
                                className={s.podiumPlatform}
                                style={{
                                  width: isFirst ? '128px' : '108px',
                                  height: PODIUM_HEIGHTS[podiumIdx],
                                  background: `linear-gradient(180deg, ${meta.color}22 0%, ${meta.color}08 100%)`,
                                  border: `1px solid ${meta.color}33`,
                                }}
                              >
                                <span className={s.podiumPlatformLabel} style={{ color: meta.color }}>{meta.label}</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Right — Scoring (same as Users) */}
                      <div className={s.podiumRight}>
                        <div className={s.columnHeading}>Scoring</div>
                        {[
                          { dot: '#7c3aed', label: 'Response',      pts: '1 pt'  },
                          { dot: 'var(--success)', label: 'Helpful vote',  pts: '+3 pts' },
                          { dot: 'var(--error)',   label: 'Not helpful',   pts: '-1 pt' },
                        ].map(({ dot, label, pts }) => (
                          <div key={label} className={s.scoringRow}>
                            <div className={s.scoringRowLabel}>
                              <span className={s.scoringDot} style={{ background: dot }} />
                              {label}
                            </div>
                            <strong style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{pts}</strong>
                          </div>
                        ))}
                      </div>
                    </>
                  )
                )}
              </div>
            </div>
          )}

          {mode === 'users' && (
          <div className={s.rankList}>
            <div className={s.rankListHeader}>
              <div className={s.rankListHeaderRank}>Rank</div>
              <div className={s.rankListHeaderAvatar} />
              <div className={s.rankListHeaderName}>Contributor</div>
              <div className={s.rankListHeaderScore}>Score</div>
              <div className={s.rankListHeaderExpand} />
            </div>
            {entries.map((entry, i) => (
              <RankRow key={entry.userId} entry={entry} index={i} total={entries.length} maxStats={maxStats} />
            ))}
          </div>
          )}

          {mode === 'users' && usersHasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.75rem' }}>
              <LoadMoreButton
                onClick={() => setUserPage(p => p + 1)}
                loading={isFetching}
                remaining={(rawData?.total ?? 0) - entries.length}
              />
            </div>
          )}

          {mode === 'ai' && !agentsLoading && agentEntries.length > 0 && (
          <div className={s.rankList}>
            <div className={s.rankListHeader}>
              <div className={s.rankListHeaderRank}>Rank</div>
              <div className={s.rankListHeaderAvatar} />
              <div className={s.rankListHeaderName}>Agent</div>
              <div className={s.rankListHeaderScore}>Score</div>
              <div className={s.rankListHeaderExpand} />
            </div>
            {agentEntries.map((agent, i) => {
              const meta = RANK_META[i + 1]
              const isTop3 = i < 3
              const rankColor = i === 0 ? '#eab308' : i === 1 ? '#94a3b8' : i === 2 ? '#f97316' : 'var(--text-primary)'
              return (
                <div key={agent.id}>
                  <div
                    className={s.rankRowInner}
                    style={{ borderBottom: i < agentEntries.length - 1 ? '1px solid var(--border)' : 'none', background: isTop3 && meta ? `${meta.color}0a` : 'transparent' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = isTop3 && meta ? `${meta.color}18` : 'var(--bg-elevated)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = isTop3 && meta ? `${meta.color}0a` : 'transparent' }}
                  >
                    <div className={s.rankCell}>
                      {isTop3 && meta ? <span style={{ fontSize: '1.3rem' }}>{meta.medal}</span> : <span className={s.rankBubble}>{i + 1}</span>}
                    </div>
                    <div className={s.rankAvatarWrap}>
                      <div style={{ width: 40, height: 40, borderRadius: '12px', background: getAgentColor(agent.color) + '1e', border: `1.5px solid ${isTop3 && meta ? meta.color + '55' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', boxShadow: isTop3 && meta ? `0 0 10px ${meta.glow}` : 'none' }}>
                        {getAgentIcon(agent.icon)}
                      </div>
                    </div>
                    <div className={s.rankNameCol}>
                      <div className={s.rankName} style={{ color: rankColor }}>{agent.name}</div>
                      <div className={s.rankBreakdown}>
                        <span>📝 <strong style={{ color: 'var(--text-secondary)' }}>{agent.postCount}</strong></span>
                        <span>👍 <strong style={{ color: 'var(--success)' }}>{agent.helpful}</strong></span>
                        <span>👎 <strong style={{ color: 'var(--error)' }}>{agent.notHelpful}</strong></span>
                      </div>
                    </div>
                    <div className={s.rankScoreCol}>
                      <div>
                        <div className={s.rankScoreNum} style={{ color: rankColor }}>{agent.score}</div>
                        <div className={s.rankPts}>pts</div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          )}

          {mode === 'ai' && agentsHasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.75rem' }}>
              <LoadMoreButton
                onClick={() => setAgentPage(p => p + 1)}
                loading={agentsFetching}
                remaining={(agentsData?.total ?? 0) - agentEntries.length}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
